import { fromApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import Button from '@liquidweb-dev/undercurrent/Button';
import type { DialogProps as UndercurrentDialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import ArrowRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowRight';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import toast from '@liquidweb-dev/undercurrent/toast';
import { AcronisBackupAddon } from 'components/DialogDestroyAddon/AddOnType';
import CancellationAlert from 'components/DialogDestroyAddon/CancellationAlert';
import DestroyConfirmation from 'components/DialogDestroyAddon/DestroyConfirmation';
import HookedSubmit from 'components/Form/HookedSubmit';
import LWForm from 'components/LWForm';
import dayjs from 'dayjs';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useAssetRemove from 'modules/queries/asset/useRemove';
import { useState } from 'react';
import { dateMonthYear } from 'utility/constants/dateTime';
import DialogDestroyAddon from '../DialogDestroyAddon';
import {
	ADDON_FORM_VALIDATOR,
	INITIAL_FORM_ERRORS,
	INITIAL_FORM_VALUES,
} from '../DialogDestroyAddon/constant';
import ReasonList from '../DialogDestroyAddon/ReasonList';

export type DialogDestroyAcronisProps = Omit<
	UndercurrentDialogProps,
	'onClose'
> & {
	uniqId: string;
	onClose: () => void;
};

const DialogDestroyAcronis: React.FC<DialogDestroyAcronisProps> = ({
	uniqId,
	onClose,
	...dialogProps
}) => {
	const [isDestroyConfirmationOpen, setDestroyConfirmationOpen] =
		useState(false);
	const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
	const [formErrors, setFormErrors] = useState(INITIAL_FORM_ERRORS);
	const { open } = dialogProps;

	const { data: acronisAssetDetails } = useAssetDetails(
		{
			uniq_id: uniqId,
			alsowith: [
				'featureDetails',
				'acronisBackingUp',
				'product',
				'capabilities',
				'scheduledCancellationDate',
				'nextInvoiceDate',
			],
		},
		{ enabled: Boolean(uniqId && open) },
	);

	const { mutate: removeAsset, isLoading: isDestroyLoading } = useAssetRemove();

	const hostnames =
		acronisAssetDetails?.acronisBackingUp
			?.map((item: { domain: string }) => item.domain)
			?.join('') ?? '';

	const canScheduleTerminate = fromApiBoolean(
		acronisAssetDetails?.capabilities?.canScheduleTerminate,
	);
	/**
	 * An asset that cannot have its termination scheduled is destroyed as soon
	 * as the customer confirms, so it ends today rather than at the next
	 * invoice.
	 */
	const cancellationDate = (
		canScheduleTerminate
			? dayjs(acronisAssetDetails?.nextInvoiceDate?.DateTime)
			: dayjs()
	).format(dateMonthYear);

	const handleDestroyConfirmation = () => {
		removeAsset(
			{
				uniq_id: uniqId,
				cancellation_reason: String(formValues.cancellation_reason),
				cancellation_comment:
					String(formValues.additional_info).length > 0
						? String(formValues.additional_info)
						: undefined,
				cleanup_assets: 0,
			},
			{
				onSuccess: () => {
					handleClose();
					toast.success('Service cancelled', {
						description: `Service for Acronis Backups: ${hostnames} has been cancelled.`,
					});
				},
			},
		);
	};

	const handleScheduleCancellation = () => {
		removeAsset(
			{
				uniq_id: uniqId,
				cancellation_reason: String(formValues.cancellation_reason),
				cancellation_comment:
					String(formValues.additional_info).length > 0
						? String(formValues.additional_info)
						: undefined,
				schedule_terminate: 1,
				cleanup_assets: 0,
			},
			{
				onSuccess: () => {
					handleClose();
					toast.success('Cancellation scheduled', {
						description: `Service for Acronis Backups: ${hostnames} will end on ${cancellationDate}.`,
					});
				},
			},
		);
	};

	const handleClose = () => {
		setFormValues(INITIAL_FORM_VALUES);
		setFormErrors(INITIAL_FORM_ERRORS);
		setDestroyConfirmationOpen(false);
		onClose();
	};

	return (
		<DialogDestroyAddon onClose={handleClose} {...dialogProps}>
			{!isDestroyConfirmationOpen ? (
				<LWForm
					values={formValues}
					onValuesChange={setFormValues}
					errors={formErrors}
					validators={ADDON_FORM_VALIDATOR}
					onErrorsChange={setFormErrors}
					onSubmit={handleScheduleCancellation}
				>
					<Stack sx={{ gap: 400 }}>
						<Stack sx={{ gap: 200 }}>
							<Stack sx={{ gap: 400 }}>
								<CancellationAlert
									cancellationDate={cancellationDate}
									canScheduleTerminate={canScheduleTerminate}
								/>
								<Stack
									sx={{
										gap: 400,
									}}
								>
									<ReasonList />
									<AcronisBackupAddon uniqId={uniqId} />
								</Stack>
							</Stack>
							<Stack sx={{ gap: 100 }}>
								{canScheduleTerminate ? (
									<HookedSubmit
										size="large"
										disabled={isDestroyLoading}
										loading={isDestroyLoading}
										fullWidth
									>
										Schedule cancellation
									</HookedSubmit>
								) : (
									<Button
										size="large"
										fullWidth
										disabled={
											isDestroyLoading || formValues.cancellation_reason === ''
										}
										loading={isDestroyLoading}
										onClick={() => setDestroyConfirmationOpen(true)}
									>
										Continue
									</Button>
								)}
								<Button variant="secondary" size="large" onClick={onClose}>
									Keep service
								</Button>
							</Stack>
						</Stack>
						{canScheduleTerminate && (
							<Button
								color="danger"
								variant="link"
								size="small"
								endIcon={<ArrowRightIcon />}
								sx={{
									textDecoration: 'none',
									'&:hover': {
										textDecoration: 'none',
									},
								}}
								onClick={() => setDestroyConfirmationOpen(true)}
								disabled={
									isDestroyLoading || formValues.cancellation_reason === ''
								}
							>
								Destroy now instead
							</Button>
						)}
					</Stack>
				</LWForm>
			) : (
				<DestroyConfirmation
					isLoading={isDestroyLoading}
					uniqId={uniqId}
					addonName={`Acronis Backups: ${hostnames}`}
					onDestroy={handleDestroyConfirmation}
					onBack={() => setDestroyConfirmationOpen(false)}
				/>
			)}
		</DialogDestroyAddon>
	);
};

export default DialogDestroyAcronis;
